import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createStarterClassicQuiz } from "@quizshow/module-classic";
import { createStarterQuizBoard } from "@quizshow/module-quizboard";
import { createStarterWeakestLinkQuiz } from "@quizshow/module-weakest-link";
import type { ModuleId, QuizSet } from "@quizshow/protocol";
import { joinRoom } from "../api";
import { Brand } from "../components/brand";
import { ErrorBanner } from "../components/status";
import { importQuizFile } from "../import-export";
import { storeSession } from "../session";
import { deleteQuiz, listQuizzes, saveQuiz } from "../storage/quiz-store";

const moduleLabels: Record<ModuleId, string> = {
  classic: "Classic Quiz",
  "weakest-link": "Der Dümmste fliegt raus",
  quizboard: "Quizboard",
};

function quizItemCount(quiz: QuizSet): number {
  return quiz.moduleId === "quizboard"
    ? quiz.categories.reduce((total, category) => total + category.cells.length, 0)
    : quiz.questions.length;
}

export function HomePage() {
  const navigate = useNavigate();
  const importRef = useRef<HTMLInputElement>(null);
  const [quizzes, setQuizzes] = useState<QuizSet[]>([]);
  const [roomCode, setRoomCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const refresh = async () => setQuizzes(await listQuizzes());
  useEffect(() => {
    void refresh();
  }, []);

  const createQuiz = async (moduleId: ModuleId) => {
    const quiz =
      moduleId === "classic"
        ? createStarterClassicQuiz()
        : moduleId === "weakest-link"
          ? createStarterWeakestLinkQuiz()
          : createStarterQuizBoard();
    await saveQuiz(quiz);
    navigate(`/editor/${quiz.quizId}`);
  };

  const removeQuiz = async (quizId: string) => {
    await deleteQuiz(quizId);
    await refresh();
  };

  const importFile = async (file: File | undefined) => {
    if (!file) return;
    setError(undefined);
    try {
      const quiz = await importQuizFile(file);
      await refresh();
      navigate(`/editor/${quiz.quizId}`);
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : "Import fehlgeschlagen.");
    }
  };

  const join = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const response = await joinRoom(roomCode, displayName);
      storeSession({
        roomId: response.roomId,
        token: response.token,
        role: "participant",
        playerId: response.playerId,
      });
      navigate(`/play/${response.roomId}`);
    } catch (joinError) {
      setError(joinError instanceof Error ? joinError.message : "Beitritt fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="shell">
      <Brand />
      <ErrorBanner message={error} onClose={() => setError(undefined)} />
      <section className="hero">
        <div>
          <span className="eyebrow">Crimson Moon</span>
          <h1>Deine Quizshow. Live, modular und bereit für den Stream.</h1>
          <p>Baue Classic-Runden, ein Ausscheidungsspiel oder ein vollständiges Quizboard direkt im Browser.</p>
          <div className="button-row button-row--wrap">
            <button className="button button--primary" onClick={() => void createQuiz("classic")}>
              Neues Classic Quiz
            </button>
            <button className="button button--primary" onClick={() => void createQuiz("weakest-link")}>
              Neuer Ausscheidungsmodus
            </button>
            <button className="button button--primary" onClick={() => void createQuiz("quizboard")}>
              Neues Quizboard
            </button>
            <button className="button button--ghost" onClick={() => importRef.current?.click()}>
              Quiz importieren
            </button>
            <input
              ref={importRef}
              className="visually-hidden"
              type="file"
              accept=".qss,.json,application/json"
              onChange={(event) => void importFile(event.target.files?.[0])}
            />
          </div>
        </div>
        <form className="card join-card" onSubmit={(event) => void join(event)}>
          <h2>Raum beitreten</h2>
          <label>
            Raumcode
            <input
              value={roomCode}
              onChange={(event) => setRoomCode(event.target.value.toUpperCase().slice(0, 6))}
              placeholder="ABC123"
              required
              maxLength={6}
            />
          </label>
          <label>
            Dein Name
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Spielername"
              required
              maxLength={24}
            />
          </label>
          <button className="button button--accent" disabled={busy}>
            {busy ? "Beitritt läuft..." : "Beitreten"}
          </button>
        </form>
      </section>

      <section className="section">
        <div className="section__heading">
          <div>
            <span className="eyebrow">Lokale Bibliothek</span>
            <h2>Deine Quizsets</h2>
          </div>
          <span className="muted">{quizzes.length} gespeichert</span>
        </div>
        <div className="quiz-grid">
          {quizzes.length === 0 ? (
            <div className="card empty-state">Noch kein Quiz gespeichert. Wähle oben einen Spielmodus.</div>
          ) : (
            quizzes.map((quiz) => (
              <article className="card quiz-card" key={quiz.quizId}>
                <span className="badge">{moduleLabels[quiz.moduleId]}</span>
                <h3>{quiz.title}</h3>
                <p>{quizItemCount(quiz)} Fragenfelder</p>
                <div className="button-row">
                  <button className="button button--primary" onClick={() => navigate(`/editor/${quiz.quizId}`)}>
                    Öffnen
                  </button>
                  <button className="button button--ghost" onClick={() => void removeQuiz(quiz.quizId)}>
                    Löschen
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </main>
  );
}
